import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, Clock, Video, MessageSquare, Sparkles, ArrowRight, ClipboardCheck, Users, BookHeart, ShieldCheck, Clock3, Wifi, WifiOff, Target, Smile, BookOpen, TrendingUp, Trash2, Check, FileText, Megaphone, LayoutDashboard, UserCheck, Phone, Mail, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ParentLinkPanel, ChildLinkRequests } from "../components/ParentLink";
import PresenceDot from "../components/PresenceDot";
import PushToggle from "../components/PushToggle";
import ContributionCard from "../components/ContributionCard";
import AdminUpcomingList from "../components/AdminUpcomingList";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}
function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function isUpcoming(iso) {
  try { return new Date(iso).getTime() > Date.now() - 30 * 60 * 1000; } catch { return false; }
}

/**
 * Compact contact card for a person on a booking (client or counsellor). Shown inside
 * BookingCard when the viewer is allowed to see contact details for the other party.
 * All emails/phones are click-to-action (mailto:/tel:) so ops can reach out in one tap.
 */
const ContactStrip = ({ label, person, showPhone }) => {
  if (!person) return null;
  return (
    <div className="rounded-2xl border border-[#3D405B]/10 bg-muted/40 p-3">
      <div className="text-[10px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96] mb-1">{label}</div>
      <div className="font-bold text-[#3D405B] text-sm mb-1">{person.name || "—"}</div>
      <div className="text-[11px] text-[#5F627D] space-y-0.5">
        {person.email && (
          <a
            href={`mailto:${person.email}`}
            className="inline-flex items-center gap-1 hover:text-primary max-w-full truncate"
            data-testid={`contact-email-${person.user_id || label.toLowerCase()}`}
          >
            <Mail size={11} /> <span className="truncate">{person.email}</span>
          </a>
        )}
        {showPhone && person.phone && (
          <div>
            <a
              href={`tel:${person.phone}`}
              className="inline-flex items-center gap-1 hover:text-primary"
              data-testid={`contact-phone-${person.user_id || label.toLowerCase()}`}
            >
              <Phone size={11} /> {person.phone}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending_acceptance: { c: "bg-primary/15 text-primary border-primary/30", t: "Awaiting confirmation" },
    scheduled: { c: "bg-secondary/15 text-secondary border-secondary/30", t: "Scheduled" },
    declined: { c: "bg-destructive/10 text-destructive border-destructive/20", t: "Declined" },
    auto_declined: { c: "bg-destructive/10 text-destructive border-destructive/20", t: "Expired" },
    completed: { c: "bg-accent/25 text-[#3D405B] border-accent/40", t: "Completed" },
    cancelled: { c: "bg-destructive/10 text-destructive border-destructive/20", t: "Cancelled" },
    no_show: { c: "bg-muted text-[#5F627D] border-[#3D405B]/10", t: "No-show" },
    no_show_client: { c: "bg-muted text-[#5F627D] border-[#3D405B]/10", t: "Client no-show" },
    no_show_counsellor: { c: "bg-muted text-[#5F627D] border-[#3D405B]/10", t: "Counsellor no-show" },
    no_show_both: { c: "bg-muted text-[#5F627D] border-[#3D405B]/10", t: "No-show" },
  };
  const s = map[status] || map.scheduled;
  return <span className={`pill border ${s.c}`}>{s.t}</span>;
};

// Human-friendly "in Xd Yh" / "in Xh Ymin" / "in Xmin" countdown
function humanCountdown(iso, nowMs) {
  if (!iso) return "";
  const diffMs = new Date(iso).getTime() - nowMs;
  if (diffMs < -30 * 60 * 1000) return "";  // more than 30 min in the past
  if (diffMs < 0) return "happening now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "starting now";
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `in ${hours}h ${mins - hours * 60}m`;
  const days = Math.floor(hours / 24);
  return days === 1 ? `tomorrow (in ${hours}h)` : `in ${days} days`;
}

/**
 * Hero card shown at the top of the dashboard when the user has at least one upcoming
 * session. Gives them the ONE piece of info they most need: what's next and how to get there.
 * When the top item is a `pending_acceptance` booking and the viewer is a counsellor, we
 * flip into an amber "action needed" variant so they can't miss that the booking still needs
 * their decision (previously it looked identical to a confirmed one, so counsellors were
 * skipping past unaccepted invites and letting them auto-expire).
 */
const NextSessionHero = ({ session, role, nowMs, onAccept }) => {
  if (!session) return null;
  const isPending = session.status === "pending_acceptance";
  const isCounsellorPending = isPending && role === "counsellor";
  const other = role === "counsellor" ? session.client : session.counsellor;
  const countdown = humanCountdown(session.scheduled_at, nowMs);
  const deadlineCountdown = humanCountdown(session.acceptance_deadline, nowMs);
  const isOnline = session.session_mode !== "in_person";
  const minsToStart = Math.round((new Date(session.scheduled_at).getTime() - nowMs) / 60000);
  const canJoinNow = role === "counsellor" || role === "admin" || session.started_at || minsToStart <= 10;
  const primaryLabel = role === "counsellor"
    ? (session.started_at ? "Open live session" : "Open session")
    : (canJoinNow ? "Join now" : "Open session");

  if (isCounsellorPending) {
    return (
      <div
        className="brand-card p-6 sm:p-8 mb-8 relative overflow-hidden bg-gradient-to-br from-white via-[#FFF3E4] to-[#F2CC8F]/40 border-2 border-primary/40"
        data-testid="next-session-hero-pending"
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 pill bg-primary text-white font-extrabold shadow-sm">
                <AlertTriangle size={13} /> Action needed
              </span>
              <span className="overline text-primary">Booking request awaiting your decision</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#3D405B] tracking-tight mb-1" data-testid="hero-pending-time">
              {fmtDate(session.scheduled_at)} · {fmtTime(session.scheduled_at)}
            </h2>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[#5F627D] mt-2 text-sm">
              <span className="font-bold text-[#3D405B]">from {other?.name || "a client"}</span>
              <span className="text-[#3D405B]/30">·</span>
              <span className="inline-flex items-center gap-1.5">
                {isOnline ? <Video size={14} className="text-primary" /> : <Clock size={14} className="text-primary" />}
                {isOnline ? "Online video" : "In person"}
              </span>
              {session.topic && (
                <>
                  <span className="text-[#3D405B]/30">·</span>
                  <span className="italic truncate max-w-[280px]" title={session.topic}>&ldquo;{session.topic}&rdquo;</span>
                </>
              )}
            </div>
            {session.acceptance_deadline && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/70 border border-primary/30 px-3 py-1.5 text-xs font-extrabold text-[#B84A3E]" data-testid="hero-pending-deadline">
                <Clock3 size={13} /> Auto-declines {deadlineCountdown || "soon"} — please respond
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onAccept?.(session)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-secondary text-white font-extrabold text-base shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all whitespace-nowrap"
              data-testid="hero-accept-btn"
            >
              <Check size={18} /> Accept booking
            </button>
            <Link
              to={`/session/${session.booking_id}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border-2 border-primary/30 bg-white text-primary font-extrabold text-sm hover:bg-primary/5 transition-all whitespace-nowrap"
              data-testid="hero-review-btn"
            >
              Review & decline
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="brand-card p-6 sm:p-8 mb-8 relative overflow-hidden bg-gradient-to-br from-white via-white to-[#F2CC8F]/20 border-2 border-secondary/20"
      data-testid="next-session-hero"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-secondary/10 blur-2xl pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-secondary animate-pulse" />
            <span className="overline text-secondary">Your next session</span>
            {countdown && <span className="pill bg-secondary/15 text-secondary font-extrabold" data-testid="hero-countdown">{countdown}</span>}
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#3D405B] tracking-tight mb-1" data-testid="hero-time">
            {fmtDate(session.scheduled_at)} · {fmtTime(session.scheduled_at)}
          </h2>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[#5F627D] mt-2 text-sm">
            <span className="font-bold text-[#3D405B]">with {other?.name || "your counsellor"}</span>
            <span className="text-[#3D405B]/30">·</span>
            <span className="inline-flex items-center gap-1.5">
              {isOnline ? <Video size={14} className="text-primary" /> : <Clock size={14} className="text-primary" />}
              {isOnline ? "Online video" : "In person"}
            </span>
            {session.topic && (
              <>
                <span className="text-[#3D405B]/30">·</span>
                <span className="italic truncate max-w-[280px]" title={session.topic}>&ldquo;{session.topic}&rdquo;</span>
              </>
            )}
          </div>
        </div>
        <Link
          to={`/session/${session.booking_id}`}
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-extrabold text-base shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all whitespace-nowrap shrink-0"
          data-testid="hero-join-btn"
        >
          <Video size={18} /> {primaryLabel} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

/**
 * Compact row of role-appropriate primary actions so users don't have to hunt in the navbar
 * for the next thing they'd want to do. Two-column on mobile, four across on desktop.
 */
const QuickActions = ({ role }) => {
  const actions = (() => {
    if (role === "counsellor") return [
      { to: "/availability", label: "Availability", sub: "Add slots", icon: Calendar, tone: "primary" },
      { to: "/case-files", label: "Case files", sub: "Client notes", icon: FileText, tone: "secondary" },
      { to: "/chat", label: "Chat", sub: "Client messages", icon: MessageSquare, tone: "primary" },
      { to: "/profile", label: "My profile", sub: "Bio & specialties", icon: UserCheck, tone: "secondary" },
    ];
    if (role === "admin") return [
      { to: "/admin", label: "People", sub: "Users & applications", icon: Users, tone: "primary" },
      { to: "/admin?tab=reports", label: "Reports", sub: "Bookings · logins · geo", icon: LayoutDashboard, tone: "secondary" },
      { to: "/admin?tab=communication", label: "Broadcast", sub: "Email & WhatsApp", icon: Megaphone, tone: "primary" },
      { to: "/admin?tab=resources", label: "Resources", sub: "Self-help videos", icon: BookHeart, tone: "secondary" },
    ];
    // client & parent
    return [
      { to: "/book", label: "Book a session", sub: "Free counselling", icon: Calendar, tone: "primary" },
      { to: "/chat", label: "Chat", sub: "Talk to counsellors", icon: MessageSquare, tone: "secondary" },
      { to: "/self-help", label: "Self-help library", sub: "Guided videos", icon: BookOpen, tone: "primary" },
      { to: "/profile", label: "My profile", sub: "Contact & settings", icon: UserCheck, tone: "secondary" },
    ];
  })();
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="quick-actions">
      {actions.map((a) => {
        const Icon = a.icon;
        const iconTone = a.tone === "primary" ? "text-primary" : "text-secondary";
        return (
          <Link
            key={a.to + a.label}
            to={a.to}
            className="brand-card brand-card-hover p-4 flex items-start gap-3 group"
            data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className={`w-10 h-10 rounded-2xl bg-white flex items-center justify-center shrink-0 border border-[#3D405B]/10 ${iconTone}`}>
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-extrabold text-[#3D405B] text-sm leading-tight">{a.label}</div>
              <div className="text-xs text-[#7A7D96] mt-0.5">{a.sub}</div>
            </div>
            <ArrowRight size={14} className="text-[#7A7D96] shrink-0 mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        );
      })}
    </div>
  );
};

const BookingCard = ({ b, role, onCancel, onMarkComplete, onAccept, onDecline, onOutcome, nowMs }) => {
  const other = role === "counsellor" ? b.client : b.counsellor;
  const upcoming = isUpcoming(b.scheduled_at) && b.status === "scheduled";
  const isPending = b.status === "pending_acceptance";
  // Feedback pending: past-scheduled OR any no-show classification without an outcome recorded.
  const needsOutcome = role === "counsellor"
    && !b.outcome_recorded_at
    && (
      (b.status === "scheduled" && !isUpcoming(b.scheduled_at))
      || ["no_show_client", "no_show_counsellor", "no_show_both"].includes(b.status)
    );
  // Only show live presence for the counsellor half of the pair — clients don't broadcast presence to counsellors.
  const showPresence = role !== "counsellor" && other?.user_id;
  // Contact-strip visibility:
  //  - admin: sees BOTH counsellor + client details (name/email/phone) — POPIA-logged server-side.
  //  - counsellor: sees the client's details (name/email/phone) — needed for outreach & no-shows.
  //  - client/parent: only sees the counsellor's name/email; phone hidden for POPIA.
  const showAdminContacts = role === "admin";
  const showCounsellorContactsForClient = role === "counsellor";
  // "Client waiting" pill — only for the counsellor viewing an online, not-yet-started session
  // where the client's last heartbeat is within the last 90 seconds.
  const clientWaiting = (() => {
    if (role !== "counsellor") return null;
    if (b.session_mode !== "online" || b.status !== "scheduled" || b.started_at) return null;
    const lastPing = b.client_waiting_last_ping ? new Date(b.client_waiting_last_ping).getTime() : 0;
    if (!lastPing || (nowMs - lastPing) > 90_000) return null;
    const since = b.client_waiting_since ? new Date(b.client_waiting_since).getTime() : lastPing;
    const mins = Math.max(0, Math.floor((nowMs - since) / 60_000));
    return { mins };
  })();
  return (
    <div className="brand-card brand-card-hover p-6" data-testid={`booking-card-${b.booking_id}`}>
      {clientWaiting && (
        <div
          className="flex items-center gap-2 mb-3 -mt-2 px-3 py-1.5 rounded-full bg-secondary/15 text-secondary text-xs font-extrabold w-fit animate-pulse"
          data-testid={`client-waiting-pill-${b.booking_id}`}
        >
          <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_0_3px_rgba(129,178,154,0.35)]" />
          Client waiting {clientWaiting.mins > 0 ? `· ${clientWaiting.mins}m` : "now"}
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {other?.picture ? (
            <img src={other.picture} alt={other.name} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center font-extrabold text-[#3D405B]">{other?.name?.[0] || "?"}</div>
          )}
          <div>
            <div className="font-bold text-[#3D405B] flex items-center gap-2">
              {other?.name || "—"}
              {showPresence && <PresenceDot userId={other.user_id} />}
            </div>
            <div className="text-xs text-[#7A7D96]">
              {role === "client" || role === "parent" ? "Your counsellor" : b.session_type.replace("_", " ")}
            </div>
          </div>
        </div>
        <StatusBadge status={b.status} />
      </div>
      <div className="flex items-center gap-4 text-sm text-[#3D405B] mb-4 flex-wrap">
        <div className="flex items-center gap-1.5"><Calendar size={15} className="text-primary" /> {fmtDate(b.scheduled_at)}</div>
        <div className="flex items-center gap-1.5"><Clock size={15} className="text-primary" /> {fmtTime(b.scheduled_at)} · {b.duration_minutes}min</div>
        <span className={`pill ${b.session_mode === "in_person" ? "bg-accent/30 text-[#3D405B]" : "bg-primary/15 text-primary"}`} data-testid={`booking-mode-${b.booking_id}`}>
          {b.session_mode === "in_person" ? "In person" : "Online"}
        </span>
      </div>

      {/* Contact details — surfaced so admins/counsellors can reach out in one tap.
          Clients only see the counsellor's name/email (phone hidden for POPIA). */}
      {showAdminContacts && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4" data-testid={`booking-contacts-${b.booking_id}`}>
          <ContactStrip label="Counsellor" person={b.counsellor} showPhone />
          <ContactStrip label="Client" person={b.client} showPhone />
        </div>
      )}
      {showCounsellorContactsForClient && (
        <div className="mb-4" data-testid={`booking-contacts-${b.booking_id}`}>
          <ContactStrip label="Client contact" person={b.client} showPhone />
        </div>
      )}

      {b.topic && <p className="text-sm text-[#5F627D] bg-muted/50 rounded-xl p-3 mb-4">&ldquo;{b.topic}&rdquo;</p>}
      {b.summary_client && (
        <div className="mb-4 p-3 rounded-xl bg-secondary/10 border border-secondary/20">
          <div className="text-xs font-bold uppercase tracking-wider text-secondary mb-1">Session summary</div>
          <p className="text-sm text-[#3D405B]">{b.summary_client}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        {upcoming && (
          <Link to={`/session/${b.booking_id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-white text-sm font-bold shadow-sm hover:-translate-y-0.5 transition-all" data-testid={`join-session-${b.booking_id}`}>
            <Video size={15} /> Join video room
          </Link>
        )}
        <Link to={`/session/${b.booking_id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#3D405B]/15 text-[#3D405B] text-sm font-semibold hover:bg-[#3D405B]/5 transition-all" data-testid={`view-session-${b.booking_id}`}>
          Details <ArrowRight size={14} />
        </Link>
        {/* Message the other party. For clients this is the headline CTA so the label is
            explicit; for counsellors/admins the shorter "Chat" label keeps the toolbar tight. */}
        <Link
          to={`/chat?with=${other?.user_id || ""}`}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
            role === "client" || role === "parent"
              ? "bg-secondary/15 text-secondary hover:bg-secondary/25 border border-secondary/30"
              : "border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"
          }`}
          data-testid={`message-counsellor-${b.booking_id}`}
        >
          <MessageSquare size={15} /> {role === "client" || role === "parent" ? "Message counsellor" : "Chat"}
        </Link>
        {/* Client / parent: quick "Email" fallback so they can reach the counsellor
            outside the app if they prefer. Uses the counsellor's email surfaced by the
            /bookings response for their role. */}
        {(role === "client" || role === "parent") && other?.email && (
          <a
            href={`mailto:${other.email}?subject=${encodeURIComponent("Hellofoundation session on " + fmtDate(b.scheduled_at))}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#3D405B]/15 text-[#3D405B] text-sm font-semibold hover:bg-[#3D405B]/5 transition-all"
            data-testid={`email-counsellor-${b.booking_id}`}
          >
            <Mail size={15} /> Email
          </a>
        )}
        {role === "counsellor" && isPending && (
          <>
            <button
              onClick={() => onAccept?.(b)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary text-white text-sm font-extrabold shadow-sm hover:-translate-y-0.5 transition-all"
              data-testid={`accept-booking-${b.booking_id}`}
            >
              <Check size={15} /> Accept
            </button>
            <button
              onClick={() => onDecline?.(b)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-destructive/40 text-destructive text-sm font-semibold hover:bg-destructive/10 transition-all"
              data-testid={`decline-booking-${b.booking_id}`}
            >
              Decline
            </button>
          </>
        )}
        {role === "counsellor" && b.status === "scheduled" && (
          <button onClick={() => onMarkComplete(b)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-secondary/40 text-secondary text-sm font-semibold hover:bg-secondary/10 transition-all" data-testid={`complete-session-${b.booking_id}`}>
            <ClipboardCheck size={15} /> Mark complete
          </button>
        )}
        {needsOutcome && (
          <button
            onClick={() => onOutcome?.(b)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/15 text-primary text-sm font-extrabold border border-primary/25 hover:bg-primary/25 transition-all"
            data-testid={`outcome-btn-${b.booking_id}`}
          >
            <ClipboardCheck size={15} /> Record outcome
          </button>
        )}
        {b.status === "scheduled" && (
          <button onClick={() => onCancel(b)} className="text-sm text-destructive/80 hover:text-destructive px-2" data-testid={`cancel-session-${b.booking_id}`}>Cancel</button>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pending counsellor application gate — do not load bookings, show under-review screen
  const isPendingCounsellor = user?.role === "counsellor" && user?.verification_status === "pending_verification";
  const isRejectedCounsellor = user?.role === "counsellor" && user?.verification_status === "rejected";

  const load = useCallback(async () => {
    if (isPendingCounsellor || isRejectedCounsellor) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
      if (user?.role === "admin") {
        const { data: s } = await api.get("/admin/stats");
        setStats(s);
      }
    } catch (e) {
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  }, [isPendingCounsellor, isRejectedCounsellor, user?.role]);
  useEffect(() => { load(); }, [load]);

  // Counsellor-only: poll bookings every 25s AND tick every 15s so the "Client waiting"
  // pill lights up promptly when a client opens the session lobby and fades when they close it.
  // Client-side polling is cheap: /api/bookings for a single counsellor typically returns <20 rows.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (user?.role !== "counsellor") return undefined;
    const tick = setInterval(() => setNowMs(Date.now()), 15_000);
    const reload = setInterval(() => { load(); }, 25_000);
    return () => { clearInterval(tick); clearInterval(reload); };
  }, [user?.role, load]);

  const onCancel = async (b) => {
    if (!window.confirm("Cancel this session?")) return;
    try { await api.post(`/bookings/${b.booking_id}/cancel`); toast.success("Session cancelled"); load(); }
    catch (e) { toast.error("Could not cancel"); }
  };

  const onMarkComplete = async (b) => {
    const summary = window.prompt("Summary for the client (visible to them):", b.summary_client || "");
    if (summary === null) return;
    try { await api.patch(`/bookings/${b.booking_id}`, { status: "completed", summary_client: summary }); toast.success("Marked complete"); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  const onAccept = async (b) => {
    try {
      await api.post(`/bookings/${b.booking_id}/accept`);
      toast.success("Session accepted — client has been notified.");
      load();
    } catch (e) {
      toast.error("Could not accept session");
    }
  };

  const onDecline = async (b) => {
    const reason = window.prompt(
      "Optional — why are you declining? (kept internal, never shown to the client)",
      "",
    );
    if (reason === null) return;  // user hit Cancel
    if (!window.confirm("Decline this booking? The slot will be freed and the client notified.")) return;
    try {
      await api.post(`/bookings/${b.booking_id}/decline`, { reason: reason.trim() || null });
      toast.success("Session declined — client has been offered another counsellor.");
      load();
    } catch (e) {
      toast.error("Could not decline");
    }
  };

  const onOutcome = async (b) => {
    // Simple prompt-based flow. The user asked for capture, not a rich modal — this keeps
    // the interaction to two prompts and integrates cleanly with the existing dashboard.
    const yes = window.confirm("Did this session happen?\n\nOK = Yes (session went ahead)\nCancel = No (session did not happen)");
    let reason = null;
    if (!yes) {
      const choice = window.prompt(
        "What happened? Type one of:\n  client_no_show — client didn't join\n  counsellor_emergency — I couldn't make it\n  tech_issue — technical problem\n  other",
        "client_no_show",
      );
      if (choice === null) return;
      reason = (choice || "").trim() || "other";
    }
    const notes = window.prompt("Any notes for the admin? (optional)", "");
    if (notes === null) return;
    try {
      await api.post(`/bookings/${b.booking_id}/outcome`, {
        happened: yes,
        reason,
        notes: notes.trim() || null,
      });
      toast.success("Thanks — outcome recorded.");
      load();
    } catch (e) {
      toast.error("Could not save outcome");
    }
  };

  const upcoming = bookings
    // Include pending_acceptance so counsellors + clients see the pending booking at the top,
    // instead of it silently living in "past" until it's accepted.
    .filter((b) => (b.status === "scheduled" || b.status === "pending_acceptance") && isUpcoming(b.scheduled_at))
    // Soonest first — the session happening tomorrow sits above the one next week.
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
  // Counsellors get a dedicated "awaiting your acceptance" section so pending invites
  // never blend into confirmed ones. When there are pending items, prefer showing the
  // earliest pending one in the hero (over the earliest confirmed) so the counsellor's
  // very first action prompt is the decision that will auto-decline if ignored.
  const pendingForCounsellor = user?.role === "counsellor"
    ? upcoming.filter((b) => b.status === "pending_acceptance")
    : [];
  const confirmedUpcomingForCounsellor = user?.role === "counsellor"
    ? upcoming.filter((b) => b.status === "scheduled")
    : upcoming;
  const heroSession = user?.role === "counsellor"
    ? (pendingForCounsellor[0] || confirmedUpcomingForCounsellor[0])
    : upcoming[0];
  const past = bookings
    .filter((b) => !upcoming.includes(b))
    // Most recent first — yesterday sits above last month.
    .sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || ""));

  // Clients see past sessions grouped by counsellor; other roles see a flat list.
  const isClient = user?.role === "client";
  const pastByCounsellor = isClient
    ? past.reduce((acc, b) => {
        const key = b.counsellor?.name || "Unknown counsellor";
        (acc[key] = acc[key] || []).push(b);
        return acc;
      }, {})
    : null;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const roleHomeLabel = { admin: "Admin home", counsellor: "Counsellor home" }[user?.role] || "Your space";
  // Dynamic subtitle so users immediately understand what this page is for and what's going on today.
  const roleTagline = (() => {
    const upComingCount = bookings.filter((b) => b.status === "scheduled" && isUpcoming(b.scheduled_at)).length;
    const pendingCount = pendingForCounsellor.length;
    if (user?.role === "admin") return "A live overview of what's happening on Hellofoundation.";
    if (user?.role === "counsellor") {
      if (pendingCount > 0) {
        return `You have ${pendingCount} booking request${pendingCount === 1 ? "" : "s"} awaiting your decision — please accept or decline before ${pendingCount === 1 ? "it" : "they"} auto-expire${pendingCount === 1 ? "s" : ""}.`;
      }
      if (upComingCount === 0) return "No upcoming sessions. Add availability so clients can book with you.";
      return `You have ${upComingCount} upcoming session${upComingCount === 1 ? "" : "s"}. Everything you need is one click away.`;
    }
    if (user?.role === "parent") return "Your linked children, session history and shared wellness updates.";
    if (upComingCount === 0) return "You have no upcoming sessions. Book one — it's free and confidential.";
    return `Your next session is ${humanCountdown(bookings.find((b) => b.status === "scheduled" && isUpcoming(b.scheduled_at))?.scheduled_at, nowMs) || "coming up"}. Here's everything in one place.`;
  })();
  const emptyStateSub = user?.role === "counsellor"
    ? "Add availability so clients can book."
    : "Book your first session — it's free.";

  const renderEmptyStateCta = () => {
    if (user?.role === "counsellor") {
      return <Link to="/availability" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold">Manage availability</Link>;
    }
    if (user?.role === "admin") return null;
    return <Link to="/book" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold">Book a session</Link>;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      {(isPendingCounsellor || isRejectedCounsellor) ? (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="counsellor-pending-screen">
          <div className="brand-card p-10 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isRejectedCounsellor ? "bg-destructive/10 text-destructive" : "bg-accent/25 text-secondary"}`}>
              {isRejectedCounsellor ? <ShieldCheck size={30} /> : <Clock3 size={30} />}
            </div>
            <div className="overline mb-2">{isRejectedCounsellor ? "Application closed" : "Application under review"}</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight mb-3">
              {isRejectedCounsellor ? "We couldn't approve your application" : `Thanks for applying, ${user?.name?.split(" ")[0]}.`}
            </h1>
            {isRejectedCounsellor ? (
              <p className="text-[#5F627D] leading-relaxed max-w-xl mx-auto">
                We&apos;ve emailed you the outcome of your application. You&apos;re welcome to reply to that email for more detail, or to reapply in the future.
              </p>
            ) : (
              <p className="text-[#5F627D] leading-relaxed max-w-xl mx-auto">
                Our clinical team is verifying your registration with the professional body you nominated. This usually takes <strong className="text-[#3D405B]">3–5 business days</strong>. You&apos;ll get an email as soon as we&apos;re done — no need to log back in until then.
              </p>
            )}
            <div className="mt-6 text-xs text-[#7A7D96]">
              Questions? Email <a href="mailto:hello@hellofoundation.org" className="text-primary font-bold">hello@hellofoundation.org</a>
            </div>
          </div>
        </main>
      ) : (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <div className="overline mb-2">{roleHomeLabel}</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight" data-testid="dashboard-greeting">{greeting}, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-[#7A7D96] mt-2">{roleTagline}</p>
          </div>
          {user?.role !== "counsellor" && user?.role !== "admin" && (
            <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid="dashboard-book-cta">
              <Calendar size={18} /> Book a session
            </Link>
          )}
        </div>

        {/* Link request notifications for any client/parent */}
        <ChildLinkRequests onChange={() => load()} />

        {/* Parent dashboard: linked children panel */}
        {user?.role === "parent" && <ParentLinkPanel />}

        {/* Hero card — the ONE piece of info the user most wants to see: their next session.
            For counsellors we prefer the earliest pending_acceptance so an unaccepted invite
            is impossible to miss (previously it looked identical to a confirmed booking). */}
        {!loading && heroSession && (
          <NextSessionHero session={heroSession} role={user?.role} nowMs={nowMs} onAccept={onAccept} />
        )}

        {/* Quick actions — jump-off tiles for the tasks each role does most often */}
        {!loading && user?.role !== "admin" && <QuickActions role={user?.role} />}

        {/* Web Push toggle — counsellors only. Placed above the sessions list so the primary
            'get real-time alerts' CTA is visible on first paint. */}
        {!loading && user?.role === "counsellor" && <PushToggle />}

        {/* YTD contribution — hours donated, sessions delivered, people helped this year. */}
        {!loading && user?.role === "counsellor" && <ContributionCard />}

        {user?.role === "admin" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <Link to="/admin" className="brand-card brand-card-hover p-5">
              <Users size={22} className="text-primary mb-2" />
              <div className="text-3xl font-extrabold text-[#3D405B]">{stats.total_users}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A7D96] font-bold mt-1">People onboard</div>
            </Link>
            <div className="brand-card p-5">
              <Calendar size={22} className="text-secondary mb-2" />
              <div className="text-3xl font-extrabold text-[#3D405B]">{stats.upcoming_sessions}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A7D96] font-bold mt-1">Upcoming sessions</div>
            </div>
            <div className="brand-card p-5">
              <ClipboardCheck size={22} className="text-primary mb-2" />
              <div className="text-3xl font-extrabold text-[#3D405B]">{stats.completed_sessions}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A7D96] font-bold mt-1">Sessions delivered</div>
            </div>
            <Link to="/admin?tab=resources" className="brand-card brand-card-hover p-5">
              <BookHeart size={22} className="text-secondary mb-2" />
              <div className="text-3xl font-extrabold text-[#3D405B]">{stats.total_resources}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A7D96] font-bold mt-1">Self-help videos</div>
            </Link>
          </div>
        )}

        {user?.role === "admin" && <AdminOnlinePanel />}

        {/* Admin — Upcoming sessions list with pending vs accepted badges + click-through
            to a contact modal (phone/WhatsApp/email) so admins can call to confirm. */}
        {!loading && user?.role === "admin" && <AdminUpcomingList />}

        {/* Admin quick actions — placed after stats/presence so the ops overview stays on top */}
        {!loading && user?.role === "admin" && <QuickActions role="admin" />}

        {isClient && <WellnessDashboard />}

        {/* Counsellor-only "awaiting your acceptance" section — pending bookings live
            here in an amber-styled block so they can't be confused with confirmed ones.
            Rendered above "Upcoming sessions" so it's the first thing they see. */}
        {!loading && user?.role === "counsellor" && pendingForCounsellor.length > 0 && (
          <section className="mb-10" data-testid="pending-acceptance-section">
            <div className="flex items-end justify-between gap-3 mb-4">
              <div>
                <h2 className="text-2xl font-extrabold text-[#3D405B] flex items-center gap-2">
                  <AlertTriangle size={20} className="text-primary" />
                  Booking requests — awaiting your acceptance
                </h2>
                <p className="text-sm text-[#7A7D96] mt-0.5">
                  These clients booked you but the session isn't confirmed yet. Accept or decline before each request auto-expires.
                </p>
              </div>
              <span
                className="pill bg-primary text-white shrink-0 font-extrabold"
                data-testid="pending-count"
              >
                {pendingForCounsellor.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {pendingForCounsellor.map((b) => (
                <BookingCard
                  key={b.booking_id}
                  b={b}
                  role={user?.role}
                  onCancel={onCancel}
                  onMarkComplete={onMarkComplete}
                  onAccept={onAccept}
                  onDecline={onDecline}
                  onOutcome={onOutcome}
                  nowMs={nowMs}
                />
              ))}
            </div>
          </section>
        )}

        <section className="mb-10">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-extrabold text-[#3D405B]">
                {user?.role === "counsellor" ? "Confirmed upcoming sessions" : "Upcoming sessions"}
              </h2>
              <p className="text-sm text-[#7A7D96] mt-0.5">
                {user?.role === "counsellor"
                  ? "Sessions you've accepted, soonest first."
                  : "Confirmed bookings — soonest first. Tap Open session when it's time."}
              </p>
            </div>
            <span className="pill bg-primary/10 text-primary shrink-0" data-testid="upcoming-count">{confirmedUpcomingForCounsellor.length}</span>
          </div>
          {loading ? (
            <div className="brand-card p-10 text-center text-[#7A7D96]">Loading…</div>
          ) : confirmedUpcomingForCounsellor.length === 0 ? (
            <div className="brand-card p-10 text-center">
              <Sparkles className="mx-auto text-accent mb-3" size={28} />
              <div className="font-bold text-[#3D405B]">
                {user?.role === "counsellor" && pendingForCounsellor.length > 0
                  ? "No confirmed sessions yet"
                  : "No upcoming sessions yet"}
              </div>
              <p className="text-sm text-[#7A7D96] mt-1">
                {user?.role === "counsellor" && pendingForCounsellor.length > 0
                  ? "Accept a request above to confirm your next session."
                  : emptyStateSub}
              </p>
              {!(user?.role === "counsellor" && pendingForCounsellor.length > 0) && (
                <div className="mt-4">{renderEmptyStateCta()}</div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {confirmedUpcomingForCounsellor.map((b) => (
                <BookingCard key={b.booking_id} b={b} role={user?.role} onCancel={onCancel} onMarkComplete={onMarkComplete} onAccept={onAccept} onDecline={onDecline} onOutcome={onOutcome} nowMs={nowMs} />
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-extrabold text-[#3D405B]">Past sessions</h2>
              <p className="text-sm text-[#7A7D96] mt-0.5">
                {isClient
                  ? "Sessions you've had, grouped by counsellor. Shared summaries appear on each card."
                  : user?.role === "counsellor"
                    ? "Sessions you've completed or that were cancelled. Open a card to add notes."
                    : "Completed, cancelled and no-show sessions across the platform."}
              </p>
            </div>
            <span className="pill bg-muted text-[#5F627D] shrink-0" data-testid="past-count">{past.length}</span>
          </div>
          {past.length === 0 ? (
            <div className="text-sm text-[#7A7D96]">No past sessions yet.</div>
          ) : isClient && pastByCounsellor ? (
            <div className="space-y-8" data-testid="past-sessions-by-counsellor">
              {Object.entries(pastByCounsellor).map(([counsellorName, list]) => (
                <div key={counsellorName}>
                  <h3 className="text-base font-extrabold text-[#3D405B] mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    with {counsellorName}
                    <span className="pill bg-muted text-[#5F627D]">{list.length}</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {list.map((b) => (
                      <BookingCard key={b.booking_id} b={b} role={user?.role} onCancel={onCancel} onMarkComplete={onMarkComplete} onAccept={onAccept} onDecline={onDecline} onOutcome={onOutcome} nowMs={nowMs} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {past.map((b) => (
                <BookingCard key={b.booking_id} b={b} role={user?.role} onCancel={onCancel} onMarkComplete={onMarkComplete} onAccept={onAccept} onDecline={onDecline} onOutcome={onOutcome} nowMs={nowMs} />
              ))}
            </div>
          )}
        </section>
      </main>
      )}
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return "—"; }
}

function AdminOnlinePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/online-status");
        if (mounted) setData(data);
      } catch (err) { logger.warn("online-status load failed:", err?.message || err); }
      finally { if (mounted) setLoading(false); }
    };
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (loading || !data) {
    return (
      <div className="brand-card p-6 mb-8" data-testid="admin-online-panel">
        <div className="text-sm text-[#7A7D96]">Loading presence…</div>
      </div>
    );
  }

  const roleMeta = {
    client: { label: "Clients", tone: "text-primary bg-primary/10 border-primary/20" },
    counsellor: { label: "Counsellors", tone: "text-secondary bg-secondary/15 border-secondary/25" },
    parent: { label: "Parents", tone: "text-[#3D405B] bg-muted border-[#3D405B]/10" },
    admin: { label: "Admins", tone: "text-[#3D405B] bg-accent/25 border-accent/40" },
  };

  // Show most recently active users first
  const recent = [...data.users].sort((a, b) => {
    if (a.is_online !== b.is_online) return a.is_online ? -1 : 1;
    return (b.last_login_at || "").localeCompare(a.last_login_at || "");
  }).slice(0, 8);

  return (
    <div className="brand-card p-6 mb-8" data-testid="admin-online-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center">
          <Wifi size={18} />
        </div>
        <div>
          <h2 className="text-lg font-extrabold text-[#3D405B]">Live presence</h2>
          <p className="text-xs text-[#7A7D96]">Auto-refreshes every 30s · last check just now</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <span className="pill bg-secondary/15 text-secondary inline-flex items-center gap-1"><Wifi size={11} /> {data.totals.online} online</span>
          <span className="pill bg-muted text-[#7A7D96] inline-flex items-center gap-1"><WifiOff size={11} /> {data.totals.offline} offline</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {["client", "counsellor", "parent", "admin"].map((r) => {
          const v = data.by_role[r] || { online: 0, offline: 0, total: 0, never_logged_in: 0 };
          const m = roleMeta[r];
          return (
            <div key={r} className={`rounded-2xl border p-3 ${m.tone}`} data-testid={`presence-role-${r}`}>
              <div className="text-[11px] font-extrabold uppercase tracking-wider opacity-80">{m.label}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-extrabold">{v.online}</span>
                <span className="text-xs opacity-70">/ {v.total} total</span>
              </div>
              <div className="text-[10px] opacity-70 mt-0.5">{v.offline} offline · {v.never_logged_in} never signed in</div>
            </div>
          );
        })}
      </div>

      {recent.length === 0 ? (
        <div className="text-sm text-[#7A7D96]">No signed-in users yet.</div>
      ) : (
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider text-[#7A7D96] mb-2">Most recently active</div>
          <div className="divide-y divide-[#3D405B]/5">
            {recent.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 py-2" data-testid={`presence-user-${u.user_id}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${u.is_online ? "bg-secondary shadow-[0_0_0_3px_rgba(129,178,154,0.25)]" : "bg-[#7A7D96]/40"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#3D405B] truncate">{u.name}</div>
                  <div className="text-[11px] text-[#7A7D96] truncate">{u.email} · <span className="capitalize">{u.role}</span></div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[11px] font-bold ${u.is_online ? "text-secondary" : "text-[#7A7D96]"}`}>
                    {u.is_online ? "Online now" : `Last seen ${fmtRelative(u.last_login_at)}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function WellnessDashboard() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/wellness/summary"); setSummary(data); }
    catch (err) { logger.warn("wellness summary load failed:", err?.message || err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="mb-10" data-testid="wellness-dashboard">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl font-extrabold text-[#3D405B]">Your wellness</h2>
        {summary && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="pill bg-primary/10 text-primary inline-flex items-center gap-1"><Target size={11} /> {summary.stats.goals_active} active</span>
            <span className="pill bg-secondary/15 text-secondary inline-flex items-center gap-1"><Check size={11} /> {summary.stats.goals_done} done</span>
            {summary.stats.avg_mood_last_7d != null && (
              <span className="pill bg-accent/25 text-[#3D405B] inline-flex items-center gap-1"><Smile size={11} /> {summary.stats.avg_mood_last_7d}/10 · 7d avg</span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[{ id: "overview", label: "Overview", icon: TrendingUp },{ id: "goals", label: "Goals", icon: Target },{ id: "mood", label: "Mood", icon: Smile },{ id: "journal", label: "Journal", icon: BookOpen },{ id: "history", label: "Session history", icon: Clock }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === t.id ? "bg-primary text-white" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`} data-testid={`wellness-tab-${t.id}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>
      {loading || !summary ? (
        <div className="brand-card p-8 text-center text-[#7A7D96]">Loading…</div>
      ) : (
        <>
          {tab === "overview" && <WellnessOverview summary={summary} onGo={setTab} />}
          {tab === "goals" && <GoalsPanel summary={summary} onChange={load} />}
          {tab === "mood" && <MoodPanel summary={summary} onChange={load} />}
          {tab === "journal" && <JournalPanel summary={summary} onChange={load} />}
          {tab === "history" && <SessionHistoryPanel summary={summary} />}
        </>
      )}
    </section>
  );
}

function WellnessOverview({ summary, onGo }) {
  const activeGoals = summary.goals.filter((g) => g.status === "active").slice(0, 3);
  const recentMoods = summary.moods.slice(0, 7).reverse();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="wellness-overview">
      <div className="brand-card p-5">
        <div className="flex items-center gap-2 mb-3"><Target size={16} className="text-primary" /> <span className="font-extrabold text-[#3D405B]">Active goals</span></div>
        {activeGoals.length === 0 ? (
          <button onClick={() => onGo("goals")} className="text-xs font-bold text-primary hover:underline">+ Add your first goal</button>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {activeGoals.map((g) => (
              <li key={g.goal_id} className="text-[#3D405B]">
                <div className="font-bold">{g.title}</div>
                {g.target_date && <div className="text-[10px] text-[#7A7D96]">by {new Date(g.target_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="brand-card p-5">
        <div className="flex items-center gap-2 mb-3"><Smile size={16} className="text-secondary" /> <span className="font-extrabold text-[#3D405B]">Mood — last 7</span></div>
        {recentMoods.length === 0 ? (
          <button onClick={() => onGo("mood")} className="text-xs font-bold text-primary hover:underline">+ Log your first mood</button>
        ) : (
          <div className="flex items-end gap-1.5 h-24">
            {recentMoods.map((m) => (
              <div key={m.mood_id} className="flex-1 flex flex-col items-center justify-end">
                <div className="w-full rounded-t bg-gradient-to-t from-secondary to-primary" style={{ height: `${(m.score / 10) * 100}%` }} title={`${m.score}/10`} />
                <span className="text-[9px] font-bold text-[#7A7D96] mt-1">{m.score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="brand-card p-5">
        <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-accent" /> <span className="font-extrabold text-[#3D405B]">Recent sessions</span></div>
        {summary.past_sessions.length === 0 ? (
          <div className="text-xs text-[#7A7D96]">No completed sessions yet — <Link to="/book" className="text-primary font-bold">book one</Link>.</div>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {summary.past_sessions.slice(0, 4).map((s) => (
              <li key={s.booking_id} className="text-[#3D405B]">
                <div className="font-bold text-xs">{s.counsellor?.name || "Counsellor"}</div>
                <div className="text-[10px] text-[#7A7D96]">{new Date(s.scheduled_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })} · {s.status}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function GoalsPanel({ summary, onChange }) {
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    await api.post("/wellness/goals", { title, detail, target_date: targetDate || null });
    setTitle(""); setDetail(""); setTargetDate(""); onChange();
  };
  const toggle = async (g) => {
    await api.patch(`/wellness/goals/${g.goal_id}`, { title: g.title, detail: g.detail || "", target_date: g.target_date, status: g.status === "done" ? "active" : "done" });
    onChange();
  };
  const del = async (g) => {
    if (!window.confirm(`Delete goal "${g.title}"?`)) return;
    await api.delete(`/wellness/goals/${g.goal_id}`); onChange();
  };
  return (
    <div className="brand-card p-6 space-y-4" data-testid="wellness-goals">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New goal…" className="col-span-1 sm:col-span-2 px-4 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm outline-none focus:border-primary" data-testid="goal-title-input" />
        <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className="px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm outline-none" data-testid="goal-target-date" />
      </div>
      <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} placeholder="Why this matters (optional)…" className="w-full px-4 py-2 rounded-2xl bg-white border border-[#3D405B]/15 text-sm outline-none focus:border-primary resize-none" data-testid="goal-detail-input" />
      <button onClick={add} disabled={!title.trim()} className="px-5 py-2 rounded-full bg-primary text-white text-sm font-bold disabled:opacity-40" data-testid="goal-add-btn">+ Add goal</button>
      {summary.goals.length === 0 ? (
        <div className="text-sm text-[#7A7D96]">No goals yet.</div>
      ) : (
        <div className="space-y-2 pt-2 border-t border-[#3D405B]/5">
          {summary.goals.map((g) => (
            <div key={g.goal_id} className={`flex items-start gap-3 p-3 rounded-2xl ${g.status === "done" ? "bg-secondary/10" : "bg-muted"}`} data-testid={`goal-row-${g.goal_id}`}>
              <button onClick={() => toggle(g)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${g.status === "done" ? "bg-secondary border-secondary text-white" : "border-[#3D405B]/25"}`}>
                {g.status === "done" && <Check size={14} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm ${g.status === "done" ? "text-[#7A7D96] line-through" : "text-[#3D405B]"}`}>{g.title}</div>
                {g.detail && <div className="text-xs text-[#5F627D] mt-1">{g.detail}</div>}
                {g.target_date && <div className="text-[10px] text-[#7A7D96] mt-1">Target: {new Date(g.target_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>}
              </div>
              <button onClick={() => del(g)} className="p-1 text-[#7A7D96] hover:text-destructive shrink-0" data-testid={`goal-delete-${g.goal_id}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MoodPanel({ summary, onChange }) {
  const [score, setScore] = useState(7);
  const [note, setNote] = useState("");
  const log = async () => { await api.post("/wellness/mood", { score, note }); setNote(""); onChange(); };
  const chart = summary.moods.slice(0, 30).reverse();
  return (
    <div className="brand-card p-6 space-y-4" data-testid="wellness-mood">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="text-xs font-extrabold text-[#7A7D96] uppercase tracking-wider">How are you feeling? <span className="text-primary font-extrabold">{score}/10</span></label>
          <input type="range" min={1} max={10} value={score} onChange={(e) => setScore(Number(e.target.value))} className="w-full mt-2 accent-primary" data-testid="mood-slider" />
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…" className="flex-1 px-4 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm outline-none focus:border-primary" data-testid="mood-note" />
        <button onClick={log} className="px-5 py-2 rounded-full bg-primary text-white text-sm font-bold" data-testid="mood-log-btn">Log</button>
      </div>
      {chart.length > 0 && (
        <div className="pt-4 border-t border-[#3D405B]/5">
          <div className="text-xs font-extrabold text-[#7A7D96] uppercase tracking-wider mb-2">Last {chart.length} entries</div>
          <div className="flex items-end gap-1 h-32">
            {chart.map((m) => (
              <div key={m.mood_id} className="flex-1 flex flex-col items-center justify-end min-w-[8px]" title={`${m.score}/10 · ${new Date(m.created_at).toLocaleDateString()}${m.note ? " · " + m.note : ""}`}>
                <div className="w-full rounded-t bg-gradient-to-t from-secondary to-primary" style={{ height: `${(m.score / 10) * 100}%` }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function JournalPanel({ summary, onChange }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priv, setPriv] = useState(false);
  const write = async () => {
    if (!body.trim()) return;
    await api.post("/wellness/journal", { title, body, private: priv });
    setTitle(""); setBody(""); setPriv(false); onChange();
  };
  const del = async (e) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/wellness/journal/${e.entry_id}`); onChange();
  };
  return (
    <div className="brand-card p-6 space-y-4" data-testid="wellness-journal">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)…" className="w-full px-4 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm outline-none focus:border-primary" data-testid="journal-title" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write freely — this is your space." className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 text-sm outline-none focus:border-primary resize-y" data-testid="journal-body" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs text-[#3D405B] cursor-pointer">
          <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} className="accent-primary" data-testid="journal-private-toggle" />
          <strong>Keep private</strong> — counsellors won&apos;t see this
        </label>
        <button onClick={write} disabled={!body.trim()} className="px-5 py-2 rounded-full bg-primary text-white text-sm font-bold disabled:opacity-40" data-testid="journal-save-btn">Save entry</button>
      </div>
      <div className="space-y-3 pt-3 border-t border-[#3D405B]/5">
        {summary.journals.length === 0 ? (
          <div className="text-sm text-[#7A7D96]">No entries yet.</div>
        ) : (
          summary.journals.map((e) => (
            <div key={e.entry_id} className="p-4 rounded-2xl bg-muted" data-testid={`journal-entry-${e.entry_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {e.title && <div className="font-extrabold text-[#3D405B]">{e.title}</div>}
                    {e.private && <span className="pill bg-secondary/15 text-secondary text-[10px]">Private</span>}
                    <span className="text-[10px] text-[#7A7D96] ml-auto">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#3D405B] whitespace-pre-wrap mt-2">{e.body}</p>
                </div>
                <button onClick={() => del(e)} className="p-1 text-[#7A7D96] hover:text-destructive"><Trash2 size={13} /></button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SessionHistoryPanel({ summary }) {
  return (
    <div className="brand-card p-6" data-testid="wellness-history">
      {summary.past_sessions.length === 0 ? (
        <div className="text-sm text-[#7A7D96]">No past sessions yet — <Link to="/book" className="text-primary font-bold">book your first one</Link>.</div>
      ) : (
        <div className="divide-y divide-[#3D405B]/5">
          {summary.past_sessions.map((s) => (
            <Link key={s.booking_id} to={`/sessions/${s.booking_id}`} className="flex items-center gap-3 py-3 hover:bg-[#3D405B]/5 rounded-xl px-2" data-testid={`history-session-${s.booking_id}`}>
              <div className="w-8 h-8 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Clock size={14} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#3D405B]">{s.counsellor?.name || "Counsellor"}</div>
                <div className="text-xs text-[#7A7D96]">{new Date(s.scheduled_at).toLocaleString()} · {s.status}{s.topic ? ` · ${s.topic}` : ""}</div>
              </div>
              <ArrowRight size={14} className="text-[#7A7D96]" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

